/**
 * The external marker §4.6 requires, as a decoration rather than an attribute.
 *
 * §4.6 accepts that "permissions on a Link cannot be enforced — a linked Google
 * Doc is exactly as restricted as Google decided it is", and names two
 * mitigations, both Phase 1a. This file is the first: "links display an explicit
 * external marker everywhere they appear." A reader has to be able to tell,
 * without hovering, that the next click leaves a system where access was
 * resolved for them and lands somewhere that will decide on its own.
 *
 * **Why a decoration and not a mark attribute.** `markdown/schema.ts` removes
 * `link.target`, `rel`, and `class` precisely because markdown carries a href
 * and a title and nothing else — an attribute here would either be dropped on
 * serialization or force a dialect extension, and §5.1's round trip is the thing
 * that must not bend. Externality is also not a property of the document: the
 * same href is internal on one origin and external on another, so storing it
 * would bake today's deployment into the content. Decorations live in the view,
 * are recomputed from the href on every render, and cost the document nothing.
 *
 * The same function decides the marker in read-only rendering, so a reader and
 * an author see the identical warning.
 */

import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const externalLinkPluginKey = new PluginKey('matchboxExternalLinks');

/** Marks the anchor text itself; the glyph after it is a separate widget. */
export const EXTERNAL_LINK_CLASS = 'mx-external-link';

export const EXTERNAL_LINK_LABEL = 'External link — opens outside Matchbox OS';

/**
 * Schemes that leave the application even though they have no host to compare.
 * Listed rather than inferred: `mailto:` and `tel:` are unmistakably external,
 * and treating an unrecognised scheme as external is the safe direction (see
 * below).
 */
const ALWAYS_EXTERNAL_SCHEMES: ReadonlySet<string> = new Set([
  'mailto:',
  'tel:',
  'sms:',
  'ftp:',
  'ftps:',
  'file:',
]);

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Whether following this href leaves this deployment.
 *
 * `origin` is passed rather than read so this is testable without a DOM; it
 * defaults to the page's own origin, which is the only correct answer at
 * runtime. With no origin at all — a headless render — every absolute URL is
 * external, because nothing here can prove otherwise.
 *
 * **Unparseable hrefs are external.** Over-marking a link costs a glyph;
 * under-marking one is the failure §4.6 is mitigating, so the ambiguous case
 * resolves towards the warning.
 */
export function isExternalHref(
  href: string,
  origin: string | null = globalThis.location?.origin ?? null,
): boolean {
  const value = href.trim();
  if (!value) return false;
  // An in-document anchor and a root-relative path are this application by
  // construction — there is no host to compare and no way to leave.
  if (value.startsWith('#')) return false;
  if (!HAS_SCHEME.test(value) && !value.startsWith('//')) return false;

  const scheme = HAS_SCHEME.exec(value)?.[0]?.toLowerCase();
  if (scheme && ALWAYS_EXTERNAL_SCHEMES.has(scheme)) return true;
  if (!origin) return true;

  try {
    return new URL(value, origin).origin !== new URL(origin).origin;
  } catch {
    return true;
  }
}

interface LinkRange {
  from: number;
  to: number;
  href: string;
}

/**
 * Every external link in the document, as merged ranges.
 *
 * Merging matters: a link whose text carries a bold run is two text nodes under
 * one mark, and decorating each would put two markers on one link. `mark.eq`
 * would compare the mark instances, but adjacency plus an identical href is the
 * same test and does not depend on how ProseMirror happened to split the text.
 */
export function externalLinkRanges(doc: ProseMirrorNode, origin: string | null): LinkRange[] {
  const ranges: LinkRange[] = [];

  doc.descendants((node, pos) => {
    if (!node.isText) return;
    const link = node.marks.find((mark) => mark.type.name === 'link');
    if (!link) return;
    const href = typeof link.attrs.href === 'string' ? link.attrs.href : '';
    if (!isExternalHref(href, origin)) return;

    const from = pos;
    const to = pos + node.nodeSize;
    const previous = ranges[ranges.length - 1];
    if (previous && previous.to === from && previous.href === href) {
      previous.to = to;
      return;
    }
    ranges.push({ from, to, href });
  });

  return ranges;
}

function marker(): HTMLElement {
  const span = document.createElement('span');
  span.className = 'mx-external-link-marker';
  span.setAttribute('role', 'img');
  span.setAttribute('aria-label', EXTERNAL_LINK_LABEL);
  span.title = EXTERNAL_LINK_LABEL;
  span.textContent = '↗';
  return span;
}

function decorate(doc: ProseMirrorNode, origin: string | null): DecorationSet {
  const decorations: Decoration[] = [];
  for (const range of externalLinkRanges(doc, origin)) {
    decorations.push(
      Decoration.inline(range.from, range.to, {
        class: EXTERNAL_LINK_CLASS,
        'data-external-link': 'true',
        title: `${EXTERNAL_LINK_LABEL} · ${range.href}`,
      }),
    );
    // `side: 1` keeps the glyph outside the link's own range, so typing at the
    // end of the anchor text does not land inside a decoration that is not part
    // of the document.
    decorations.push(Decoration.widget(range.to, marker, { side: 1, ignoreSelection: true }));
  }
  return DecorationSet.create(doc, decorations);
}

/**
 * Adds no node and no mark — which is the condition `markdown/schema.ts` puts on
 * anything the editor layers over the closed component set, and which
 * `roundtrip.test.ts` asserts rather than trusts.
 */
export const ExternalLinkMarker = Extension.create({
  name: 'matchboxExternalLinks',

  addProseMirrorPlugins() {
    const origin = globalThis.location?.origin ?? null;
    return [
      new Plugin({
        key: externalLinkPluginKey,
        state: {
          init: (_config, state) => decorate(state.doc, origin),
          // Recomputed only when the document changed. A selection move cannot
          // change which links are external, and rebuilding the set on every
          // caret keystroke would put a full document walk in the typing path.
          apply: (tr, value) => (tr.docChanged ? decorate(tr.doc, origin) : value),
        },
        props: {
          decorations(state) {
            return externalLinkPluginKey.getState(state) as DecorationSet | undefined;
          },
        },
      }),
    ];
  },
});
